import api from "../utils/axios";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { showToast } from "components/ui/toast";

const CAMPAIGN_FLOW_KEY = ["campaignFlow"];
const CART_KEY = ["campaignFlowCart"];

// GET dashboard list + summary counts.
// params: { status, stage, search, page, pageSize } — all optional.
// Returns the full response ({ counts, data, pagination }) since the page
// needs the counts and rows together.
export const useCampaignFlowQuery = (params = {}) => {
  return useQuery({
    queryKey: [...CAMPAIGN_FLOW_KEY, params],
    queryFn: async () => {
      const res = await api.get("/admin/campaignFlow", { params });
      return res.data || {};
    },
    // Hold the previous page on screen while the next one loads. Without this
    // the page falls back to its skeleton on every filter/search/page change,
    // which unmounts the table — and with it the search box the user is typing
    // into. (`keepPreviousData: true` is the v4 spelling and is a no-op in v5.)
    placeholderData: keepPreviousData,
  });
};

// GET single Campaign Flow record by ID (for the detail modal).
export const useCampaignFlowByIdQuery = (id, options = {}) => {
  return useQuery({
    queryKey: [...CAMPAIGN_FLOW_KEY, id],
    queryFn: async () => {
      const res = await api.get(`/admin/campaignFlow/${id}`);
      return res.data?.data || null;
    },
    enabled: !!id,
    ...options,
  });
};

// ── Cart correction ──
// A cart locks when it's sent to Campaign Flow, but stays correctable while
// the finance application is still draft. Each cart comes back with
// `editable` + an `explanation` of why it isn't.

export const useCartDetailQuery = (propertyId, options = {}) =>
  useQuery({
    queryKey: [...CART_KEY, propertyId],
    queryFn: async () => {
      const res = await api.get(`/admin/campaignFlow/cart/${propertyId}`);
      return res.data?.data || null;
    },
    enabled: !!propertyId,
    ...options,
  });

// Catalogue for the "Add items" picker, scoped to the listing agent.
export const useCartProductsQuery = (propertyId, options = {}) =>
  useQuery({
    queryKey: [...CART_KEY, propertyId, "products"],
    queryFn: async () => {
      const res = await api.get(`/admin/campaignFlow/cart/${propertyId}/products`);
      return res.data?.data || [];
    },
    enabled: !!propertyId,
    ...options,
  });

export const useCartPackagesQuery = (propertyId, options = {}) =>
  useQuery({
    queryKey: [...CART_KEY, propertyId, "packages"],
    queryFn: async () => {
      const res = await api.get(`/admin/campaignFlow/cart/${propertyId}/packages`);
      return res.data?.data || [];
    },
    enabled: !!propertyId,
    ...options,
  });

// Saves the cart AND pushes the corrected budget to Campaign Flow.
export const useUpdateCartItemsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, sequence, items, agentContribution }) => {
      const res = await api.put(`/admin/campaignFlow/cart/${propertyId}`, {
        sequence,
        items,
        agentContribution,
      });
      return res.data?.data;
    },
    onSuccess: () =>
      showToast("success", "Cart updated and Campaign Flow budget corrected"),
    onError: (err) =>
      showToast("error", err?.response?.data?.message || "Failed to update cart"),
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: [...CART_KEY, variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: CAMPAIGN_FLOW_KEY });
    },
  });
};
